import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Code, 
  Users, 
  Database,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  RefreshCw
} from 'lucide-react';
import { fetchModels, deleteModel } from '../store/modelsSlice';
import { fetchAllCodes, deleteCode } from '../store/codesSlice';
import { dashboardApi } from '../services/api';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Toast from '../components/common/Toast';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { 
    models, 
    loading: modelsLoading, 
    pagination: modelsPagination 
  } = useSelector((state) => state.models);
  const { 
    codes, 
    loading: codesLoading, 
    pagination: codesPagination 
  } = useSelector((state) => state.codes);
  
  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    type: '', // 'model' or 'code'
    item: null,
    loading: false
  });

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success' // 'success', 'error', 'warning', 'info'
  });
  
  const [stats, setStats] = useState({
    total_models: 0,
    total_codes: 0,
    official_models: 0,
    user_models: 0,
  });

  // Loading states for individual actions
  const [actionLoading, setActionLoading] = useState({
    type: '', // 'edit' or 'delete'
    id: '', // item id/code
    loading: false
  });

  // Loading states for different operations
  const [loading, setLoading] = useState({
    stats: true, // Start with loading true
    refresh: false,
    initial: true // Add initial loading state
  });

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  console.log('Dashboard render - user:', user);
  console.log('Dashboard render - isAdmin:', isAdmin);
  console.log('Dashboard render - stats:', stats);
  console.log('Dashboard render - models:', models?.length, models);
  console.log('Dashboard render - codes:', codes?.length, codes);
  console.log('Dashboard render - loading states:', { 
    initial: loading.initial, 
    modelsLoading, 
    codesLoading, 
    refresh: loading.refresh 
  });

  // Refresh stats function (defined before refreshData)
  const refreshStats = useCallback(async () => {
    try {
      const statsResponse = await dashboardApi.getStats();
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error refreshing stats:', error);
    }
  }, []);

  // Refresh data handler
  const refreshData = useCallback(async () => {
    setLoading(prev => ({ ...prev, refresh: true }));
    try {
      const promises = [
        refreshStats(),
        dispatch(fetchModels({ page: 1, per_page: 5 })),
        dispatch(fetchAllCodes({ page: 1, per_page: 5 }))
      ];
      
      await Promise.all(promises);
      
      setToast({
        show: true,
        message: 'Data refreshed successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      setToast({
        show: true,
        message: 'Failed to refresh data',
        type: 'error'
      });
    } finally {
      setLoading(prev => ({ ...prev, refresh: false }));
    }
  }, [dispatch, refreshStats]);

  // Fetch initial data and stats with better error handling
  useEffect(() => {
    console.log('Dashboard: fetchInitialData useEffect triggered');
    const fetchInitialData = async () => {
      setLoading(prev => ({ ...prev, initial: true, stats: true }));
      try {
        console.log('Dashboard: Starting to fetch stats');
        // Fetch stats first (lightweight)
        const statsResponse = await dashboardApi.getStats();
        console.log('Dashboard: Stats response:', statsResponse.data);
        setStats(statsResponse.data);
        
        console.log('Dashboard: Starting to fetch recent data');
        // Fetch recent models and codes (5 each)
        const [modelsResponse, codesResponse] = await Promise.all([
          dispatch(fetchModels({ page: 1, per_page: 5 })),
          dispatch(fetchAllCodes({ page: 1, per_page: 5 }))
        ]);
        
        console.log('Dashboard: Data fetch completed');
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setToast({
          show: true,
          message: 'Failed to load dashboard data. Please refresh the page.',
          type: 'error'
        });
      } finally {
        setLoading(prev => ({ ...prev, initial: false, stats: false }));
      }
    };

    fetchInitialData();
  }, [dispatch]);

  const statCards = [
    {
      title: 'Total Models',
      value: stats.total_models,
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Total Codes',
      value: stats.total_codes,
      icon: Code,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Official',
      value: stats.official_models,
      icon: Database,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'User Models',
      value: stats.user_models,
      icon: Users,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ];

  // Handle delete operations with better UX
  const handleDeleteClick = (type, item) => {
    setDeleteDialog({
      isOpen: true,
      type,
      item,
      loading: false
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.item) return;

    setDeleteDialog(prev => ({ ...prev, loading: true }));
    
    // Also set action loading for individual item
    const itemId = deleteDialog.type === 'model' ? deleteDialog.item.code : deleteDialog.item.id;
    setActionLoading({
      type: 'delete',
      id: itemId,
      loading: true
    });

    try {
      if (deleteDialog.type === 'model') {
        const result = await dispatch(deleteModel(deleteDialog.item.code)).unwrap();
        
        // Refresh data to update UI immediately
        const refreshPromises = [
          dispatch(fetchModels({ page: 1, per_page: 5 })),
          refreshStats()
        ];
        
        // Also refresh codes to update counts
        refreshPromises.push(dispatch(fetchAllCodes({ page: 1, per_page: 5 })));
        
        await Promise.all(refreshPromises);
        
        const deletedCodesCount = result.deleted_codes_count || 0;
        const message = deletedCodesCount > 0 
          ? `Model "${deleteDialog.item.brand} ${deleteDialog.item.model}" deleted successfully. ${deletedCodesCount} related IR codes were also deleted.`
          : `Model "${deleteDialog.item.brand} ${deleteDialog.item.model}" deleted successfully.`;
          
        setToast({
          show: true,
          message: message,
          type: 'success'
        });
      } else if (deleteDialog.type === 'code') {
        await dispatch(deleteCode(deleteDialog.item.id)).unwrap();
        
        // Refresh codes data and stats
        await Promise.all([
          dispatch(fetchAllCodes({ page: 1, per_page: 5 })),
          dispatch(fetchModels({ page: 1, per_page: 5 })),
          refreshStats()
        ]);
        
        setToast({
          show: true,
          message: `IR Code "${deleteDialog.item.name}" deleted successfully`,
          type: 'success'
        });
      }
      
      // Close dialog and reset states
      setDeleteDialog({
        isOpen: false,
        type: '',
        item: null,
        loading: false
      });
      
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error?.message || error?.toString() || 'Unknown error occurred';
      setToast({
        show: true,
        message: `Failed to delete ${deleteDialog.type}: ${errorMessage}`,
        type: 'error'
      });
      setDeleteDialog(prev => ({ ...prev, loading: false }));
    } finally {
      setActionLoading({
        type: '',
        id: '',
        loading: false
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      isOpen: false,
      type: '',
      item: null,
      loading: false
    });
  };

  const handleToastClose = () => {
    setToast({
      show: false,
      message: '',
      type: 'success'
    });
  };

  // Refresh data manually (remove duplicate)

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (event) => {
      // Only handle when not in input fields
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;
      
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'r':
            event.preventDefault();
            refreshData();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [refreshData]);

  if (loading.initial) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="large" text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards - Smaller size */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg border p-4 shadow-sm"
          >
            <div className="flex items-center">
              <div className={`rounded-lg p-2 ${stat.bgColor}`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">{stat.title}</p>
                <p className="text-lg font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Data Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent IR Models */}
        <div className="bg-white shadow-sm rounded-lg border">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Recent IR Models</h3>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Latest first</span>
                <Link
                  to="/models"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View all
                </Link>
              </div>
            </div>
          </div>
          <div className="p-6">
            {modelsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loading size="medium" text="Loading models..." />
              </div>
            ) : (models || []).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No models found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(models || []).map((model) => (
                  <div key={model.code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Package className="h-4 w-4 text-blue-600" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {model.brand} {model.model}
                          </p>
                          <p className="text-xs text-gray-500">
                            #{model.code} • {model.device_type} • {model.source}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-400">
                        {new Date(model.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-1">
                        <Link
                          to={`/models/${model.code}`}
                          className="text-blue-600 hover:text-blue-800"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        {isAdmin && (
                          <Link
                            to={`/models/${model.code}/edit`}
                            className="text-gray-600 hover:text-gray-800"
                            title="Edit model"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent IR Codes */}
        <div className="bg-white shadow-sm rounded-lg border">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Recent IR Codes</h3>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Latest first</span>
                <Link
                  to="/codes"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View all
                </Link>
              </div>
            </div>
          </div>
          <div className="p-6">
            {codesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loading size="medium" text="Loading codes..." />
              </div>
            ) : (codes || []).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Code className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No codes found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(codes || []).map((code) => (
                  <div key={code.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <Code className="h-4 w-4 text-green-600" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            #{code.id}
                            {code.model_info && (
                              <span className="ml-2 text-gray-600">
                                {code.model_info.brand} {code.model_info.model}
                              </span>
                            )}
                          </p>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            {code.power && <span>Power: {code.power}</span>}
                            {code.mode && <span>Mode: {code.mode}</span>}
                            {code.temperature && <span>Temp: {code.temperature}°C</span>}
                            {code.cmd_string && <span>{code.cmd_string}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-400">
                        {new Date(code.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex space-x-1">
                        <Link
                          to={`/codes/${code.id}`}
                          className="text-blue-600 hover:text-blue-800"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        {isAdmin && (
                          <Link
                            to={`/codes/${code.id}/edit`}
                            className="text-gray-600 hover:text-gray-800"
                            title="Edit code"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={`Delete ${deleteDialog.type === 'model' ? 'IR Model' : 'IR Code'}`}
        message={
          deleteDialog.type === 'model' && deleteDialog.item
            ? (
                <div>
                  <p className="mb-2">
                    Are you sure you want to delete the model <strong>"{deleteDialog.item.brand} {deleteDialog.item.model}"</strong>?
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Warning: This will also delete ALL related IR codes
                        </h3>
                        <p className="mt-1 text-sm text-red-700">
                          This action cannot be undone. All IR codes associated with this model will be permanently deleted.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            : deleteDialog.type === 'code' && deleteDialog.item
            ? (
                <div>
                  <p>
                    Are you sure you want to delete the IR code <strong>"#{deleteDialog.item.id}"</strong>?
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    This action cannot be undone.
                  </p>
                </div>
              )
            : `Are you sure you want to delete this ${deleteDialog.type}? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={deleteDialog.loading}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Toast Notification */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleToastClose}
        />
      )}
    </div>
  );
};

export default Dashboard;
